import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from "react";
import {
    isSignInWithEmailLink,
    onAuthStateChanged,
    signInWithEmailLink,
    signInWithPopup,
    signOut
} from "firebase/auth";
import {auth, googleAuthProvider} from "../firebase";
import {AuthState, RoutePath} from "../enums";
import {useLocation, useNavigate} from "react-router-dom";
import {User as FirebaseUser} from "firebase/auth";
import {postSendAuthEmail} from "../api";

interface IAuthContext {
    loggedInUser: FirebaseUser | null;
    authState: AuthState;
    isLoading: boolean;
    logInWithAuthLink: () => Promise<void>;
    emailAuthLink: (email: string) => Promise<void>;
    logIn: () => Promise<void>;
    logOut: () => Promise<void>;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

interface IAuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({children}) => {
    const [loggedInUser, setLoggedInUser] = useState<FirebaseUser | null>(null);
    const [authState, setAuthState] = useState<AuthState>(AuthState.NONE);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const logIn = useCallback(async () => {
        await signInWithPopup(auth, googleAuthProvider);
    }, []);

    const logInWithAuthLink = useCallback(async (): Promise<void> => {
        if (!isSignInWithEmailLink(auth, window.location.href)) {
            throw Error('An incoming link is not suitable for sign in.')
        }

        let email: string | null = window.localStorage.getItem('emailForSignIn');

        if (!email) {
            throw Error('The login link was requested on another device or used already. Please use that device to log in instead or try again.')
        }

        await signInWithEmailLink(auth, email, window.location.href)

        window.localStorage.removeItem('emailForSignIn');
    }, []);

    const emailAuthLink = useCallback(async (email: string): Promise<void> => {
        await postSendAuthEmail(email);

        window.localStorage.setItem('emailForSignIn', email);
    }, []);

    const logOut = useCallback(async () => {
        await signOut(auth);
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user: FirebaseUser | null) => {
            const isOnAdminRelatedPages = location.pathname.includes(RoutePath.ADMIN_SIGN_IN);
            setIsLoading(true);

            try {
                if (user) {
                    setLoggedInUser(user);

                    const idTokenResult = await user.getIdTokenResult();

                    if (!!idTokenResult?.claims?.isAdmin) {
                        setAuthState(AuthState.ADMIN);
                        navigate(RoutePath.ADMIN_PORTAL, {replace: true});
                    } else {
                        setAuthState(AuthState.CUSTOMER);
                        navigate(isOnAdminRelatedPages ? RoutePath.ADMIN_SIGN_IN : RoutePath.CUSTOMER_PORTAL, { replace: true });
                    }
                } else {
                    setAuthState(AuthState.NONE);
                }
            } catch (error) {
                console.error("Error updating user auth status:", error);
                await logOut();
            }

            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [logOut, navigate]);

    return (
        <AuthContext.Provider
            value={{
                authState,
                isLoading,
                logIn,
                logInWithAuthLink,
                emailAuthLink,
                logOut,
                loggedInUser
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): IAuthContext => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
};
